import Events from '@clearkit/icons/glyphs/Event';
import Pageview from '@clearkit/icons/glyphs/View';
import { CKTab, CKTabList, CKTabs } from '@clearkit/react';
import { NetworkStatus } from 'apollo-client';
import { useQuery } from 'react-apollo';

import GraphqlError from '~/components/GraphqlError';
import SearchTextField from '~/components/SearchTextField';
import Select from '~/components/Select';
import EventsList from '~/components/Sidebar/RedesignedEventsList';
import PageviewsList from '~/components/Sidebar/RedesignedPageviewsList';
import { paginateData } from '~/lib/graphql/pagination';

import query from './query.graphql';

const initialPageSize = 40;
const perPage = 20;

const PersonActivity = ({ person, className }) => {
  const [type, setType] = useState();
  const [path, setPath] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const variables = {
    first: initialPageSize,
    pageviewsFirst: initialPageSize,
    id: person.id,
  };

  if (path) variables.pageviewsPath = path;
  variables.type = type || undefined;

  const {
    data = {},
    error,
    fetchMore,
    loading,
    networkStatus,
    refetch,
  } = useQuery(query, {
    fetchPolicy: 'cache-and-network',
    variables,
    notifyOnNetworkStatusChange: true,
  });

  person = { ...person, ...data?.person };

  if (error) {
    return <GraphqlError error={error} loading={loading} refetch={refetch} />;
  }

  const {
    events,
    eventsHasNextPage,
    eventsEndCursor,
    pageviews,
    pageviewsHasNextPage,
    pageviewsEndCursor,
  } = paginateData(person, {
    fields: ['events', 'pageviews'],
  });

  const { eventTypes = [] } = data;

  const firstPageLoading = loading && networkStatus !== NetworkStatus.fetchMore;

  return (
    <div className={className}>
      <CKTabs isSegmented index={tabIndex} onChange={setTabIndex}>
        <div className="flex flex-row justify-between px-8 py-6 border-b border-gray-200 align-center bg-gray-0">
          <CKTabList aria-label="My tabs">
            <CKTab label="Pageviews" id="page" icon={<Pageview />}>
              Pageviews
            </CKTab>
            <CKTab label="Events" id="events" icon={<Events />}>
              Events
            </CKTab>
          </CKTabList>

          {tabIndex === 0 && (
            <SearchTextField
              placeholder="Search by path..."
              className="flex-grow w-6/12 max-w-md"
              onSearch={(e) => {
                let value = e.target.value;
                if (value && !value.startsWith('/')) {
                  value = `/${value}`;
                }
                setPath(value);
              }}
              value={path}
            />
          )}

          {tabIndex === 1 && (
            <Select
              className="flex-grow w-6/12 max-w-md"
              options={['', ...eventTypes]}
              getOptionValue={(option) => option || 'All'}
              getOptionLabel={(option) => option || 'All events'}
              placeholder="Filter by event type"
              onChange={(value) => setType(value)}
              isDisabled={loading}
              value={[type || 'All events']}
            />
          )}
        </div>
      </CKTabs>

      <div className="flex flex-col flex-grow">
        {tabIndex === 0 && (
          <PageviewsList
            className="px-8"
            events={pageviews}
            eventTypes={eventTypes}
            hasNextPage={pageviewsHasNextPage}
            id={person.id}
            path={path}
            loading={firstPageLoading}
            loadingMore={loading}
            onFilter={(path) => setPath(path)}
            onLoadMore={() => {
              if (loading) {
                return;
              }

              fetchMore({
                variables: {
                  ...variables,
                  pageviewsAfter: pageviewsEndCursor,
                  pageviewsFirst: perPage,
                },

                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) {
                    return prev;
                  }

                  fetchMoreResult.person.pageviews.edges = [
                    ...prev.person.pageviews.edges,
                    ...fetchMoreResult.person.pageviews.edges,
                  ];

                  return fetchMoreResult;
                },
              });
            }}
          />
        )}
        {tabIndex === 1 && (
          <EventsList
            className="flex flex-col flex-grow px-4"
            events={events}
            hasNextPage={eventsHasNextPage}
            type={type}
            id={person.id}
            loading={firstPageLoading}
            loadingMore={loading}
            onFilter={(type) => setType(type)}
            onLoadMore={() => {
              if (loading) {
                return;
              }

              fetchMore({
                variables: {
                  ...variables,
                  eventsFirst: eventsEndCursor,
                  eventsAfter: perPage,
                },

                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) {
                    return prev;
                  }

                  fetchMoreResult.person.events.edges = [
                    ...prev.person.events.edges,
                    ...fetchMoreResult.person.events.edges,
                  ];

                  return fetchMoreResult;
                },
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

PersonActivity.propTypes = {
  person: PropTypes.object.isRequired,
  className: PropTypes.string,
};

export default PersonActivity;
